import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  ApiError,
  PaginatedResponse,
  Contact,
  Event,
  EventCreate,
  EventStats,
  Invitee,
  InviteeWithDetails,
  BulkInviteRequest,
  BulkInviteResponse,
  CRMSyncResponse,
  CRMSyncLog,
  CRMSyncStats,
  MessageResponse,
  PaginationParams,
  SearchParams,
  VIPStatus,
  AudienceCount,
  CreatableEmailKind,
  EmailSend,
  EmailSendRequest,
  EventEmail,
  EventEmailContent,
  EmailBranding,
  EmailPalette,
  EmailMergeField,
  EmailPreview,
  CheckinSimulationStatus,
  CheckinSimulationRequest,
  User,
  AdminUserCreate,
  AdminUserUpdate,
  UserWithTemporaryPassword,
  AssignedEvent,
  TeamMember,
} from '../types/api';

class ApiService {
  private instance: AxiosInstance;

  constructor() {
    const apiUrl = process.env.REACT_APP_API_URL || '/api';

    this.instance = axios.create({
      baseURL: apiUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('ffm_access_token');
        console.log('🔑 Request interceptor triggered for:', config.url);
        console.log('🔑 Token exists in localStorage:', !!token);
        console.log('🔑 Token value:', token ? token.substring(0, 20) + '...' : 'null');

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          console.log('🔑 Authorization header set');
        } else {
          console.log('🔑 No token found - Authorization header NOT set');
        }

        console.log('🔑 Final headers:', config.headers);
        return config;
      },
      (error) => {
        console.log('🔑 Request interceptor error:', error);
        return Promise.reject(error);
      }
    );
  }

  // Generic HTTP methods
  async get<T>(url: string, params?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.get(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.post(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.put(url, data);
    return response.data;
  }

  async delete<T>(url: string, params?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.delete(url, { params });
    return response.data;
  }

  async validateToken(): Promise<boolean> {
    try {
      console.log('Validating current token...');
      await this.get('/auth/me');
      console.log('Token is valid');
      return true;
    } catch (error) {
      console.log('Token validation failed:', error);
      return false;
    }
  }

  // Contact API methods
  async getContacts(params?: {
    page?: number;
    page_size?: number;
    q?: string;
    status?: string;
    vip_status?: string;
    organization?: string;
    should_invite_press_conference?: boolean;
    sort_by?: string;
    sort_order?: string;
  }): Promise<PaginatedResponse<Contact>> {
    return this.get('/contacts/', params);
  }

  async getContact(id: number): Promise<Contact> {
    return this.get(`/contacts/${id}`);
  }

  async getPressConferenceContacts(): Promise<Contact[]> {
    return this.get('/contacts/press-conference/all');
  }

  async getVIPContacts(vip_level?: string): Promise<Contact[]> {
    return this.get('/contacts/vips/all', vip_level ? { vip_level } : {});
  }

  async getContactOrganizations(): Promise<string[]> {
    return this.get('/contacts/organizations');
  }

  async searchContacts(params: {
    q: string;
    limit?: number;
    exclude_event_id?: number;
  }): Promise<Contact[]> {
    return this.get('/contacts/search/suggestions', params);
  }

  async getContactStats(): Promise<any> {
    return this.get('/contacts/stats/summary');
  }

  // Dashboard
  async getActiveEventStats(): Promise<ActiveEventStats[]> {
    return this.get('/stats/active-events');
  }

  // Event API methods
  async getEvents(params?: {
    page?: number;
    page_size?: number;
    q?: string;
    status?: string;
    event_type?: string;
    featured_only?: boolean;
    upcoming_only?: boolean;
    created_by?: number;
    with_stats?: boolean;
    sort_by?: string;
    sort_order?: string;
  }): Promise<PaginatedResponse<Event>> {
    return this.get('/events/', params);
  }

  async getEvent(id: number, includeStats: boolean = true): Promise<Event> {
    const params = includeStats ? '?include_stats=true' : '';
    return this.get<Event>(`/events/${id}${params}`);
  }

  async createEvent(data: EventCreate): Promise<Event> {
    return this.post('/events/', data);
  }

  async updateEvent(id: number, data: Partial<EventCreate>): Promise<Event> {
    return this.put(`/events/${id}`, data);
  }

  async deleteEvent(id: number, force?: boolean): Promise<MessageResponse> {
    return this.delete(`/events/${id}${force ? '?force=true' : ''}`);
  }

  async publishEvent(eventId: number): Promise<Event> {
    return this.post(`/events/${eventId}/publish`);
  }

  async unpublishEvent(eventId: number): Promise<Event> {
    return this.post(`/events/${eventId}/unpublish`);
  }

  async getEventStats(id: number): Promise<EventStats> {
    return this.get(`/events/${id}/stats`);
  }

  async duplicateEvent(id: number, days_offset: number, copy_invitees?: boolean): Promise<Event> {
    const params = new URLSearchParams({
      days_offset: days_offset.toString(),
      copy_invitees: (copy_invitees || false).toString()
    });

    return this.post(`/events/${id}/duplicate?${params.toString()}`);
  }

  // Invitee API methods
  async getEventInvitees(eventId: number, params?: {
    page?: number;
    page_size?: number;
    q?: string;
    invitation_status?: string;
    response_status?: string;
    vip_only?: boolean;
    needs_reinvitation?: boolean;
    contact_status?: string;
    sort_by?: string;
    sort_order?: string;
  }): Promise<PaginatedResponse<InviteeWithDetails>> {
    return this.get(`/invitees/events/${eventId}/invitees`, params);
  }

  async inviteContactsToEvent(eventId: number, data: BulkInviteRequest): Promise<BulkInviteResponse> {
    return this.post(`/invitees/events/${eventId}/invitees`, data);
  }

  async addPressConferenceContacts(eventId: number, send_emails?: boolean): Promise<BulkInviteResponse> {
    return this.post(`/invitees/events/${eventId}/invitees/press-conference`, {
      event_id: eventId,
      send_emails_immediately: send_emails || false
    });
  }

  async getAvailableContacts(eventId: number, params?: {
    page?: number;
    page_size?: number;
    q?: string;
    press_conference_only?: boolean;
    vip_status?: string;
    organization?: string;
    sort_by?: string;
    sort_order?: string;
  }): Promise<PaginatedResponse<Contact>> {
    return this.get(`/invitees/events/${eventId}/available-contacts`, params);
  }

  async getInvitee(id: number): Promise<InviteeWithDetails> {
    return this.get(`/invitees/invitees/${id}`);
  }

  async updateInvitee(id: number, data: any): Promise<Invitee> {
    return this.put(`/invitees/invitees/${id}`, data);
  }

  async removeInvitee(id: number): Promise<MessageResponse> {
    return this.delete(`/invitees/invitees/${id}`);
  }

  async bulkDeleteInvitees(eventId: number, inviteeIds: number[]): Promise<MessageResponse> {
    return this.post(`/invitees/events/${eventId}/invitees/bulk-delete`, {
      invitee_ids: inviteeIds
    });
  }

  async deleteAllInvitees(eventId: number): Promise<MessageResponse> {
    return this.delete(`/invitees/events/${eventId}/invitees/all`, {
      confirm: 'DELETE_ALL'
    });
  }

  async getReinvitationSummary(eventId: number): Promise<any> {
    return this.get(`/invitees/events/${eventId}/reinvitation-summary`);
  }

  async resendInvitation(inviteeId: number): Promise<MessageResponse> {
    return this.post(`/invitees/invitees/${inviteeId}/resend`);
  }

  async recordManualRSVP(
    inviteeId: number,
    rsvpData: {
      response_status: 'accepted' | 'declined' | 'maybe' | 'pending';
      response_method: 'manual' | 'phone' | 'in_person';
      response_note?: string;
    }
  ): Promise<any> {
    return this.post(`/invitees/invitees/${inviteeId}/manual-rsvp`, rsvpData);
  }

  async sendInvitations(eventId: number, data: {
    invitee_ids?: number[];
    send_immediately?: boolean;
    // #60: whole-event mode, resolved server-side (batch_size/after_id page through it)
    all_pending?: boolean;
    batch_size?: number;
    after_id?: number;
    // #56: returned by the first whole-event call; pass it back so the send stays one send-log row
    send_log_id?: number;
  }): Promise<{
    success: boolean;
    message: string;
    emails_sent: number;
    emails_failed: number;
    failed_invitee_ids?: number[];
    last_invitee_id?: number;
    remaining?: number;
    send_log_id?: number | null;
  }> {
    return this.post(`/invitees/events/${eventId}/send-invitations`, data);
  }

  async sendReinvitations(eventId: number, data: {
    invitee_ids: number[];
    send_emails_immediately?: boolean;
  }): Promise<MessageResponse> {
    return this.post(`/invitees/events/${eventId}/send-reinvitations`, data);
  }

  async sendReminders(eventId: number): Promise<{
    success: boolean;
    message: string;
    reminders_sent: number;
    reminders_failed: number;
  }> {
    return this.post(`/invitees/events/${eventId}/send-reminders`);
  }

  async sendFinalConfirmations(
    eventId: number,
    inviteeIds?: number[]
  ): Promise<{
    success: boolean;
    message: string;
    stats: {
      sent: number;
      failed: number;
      skipped: number;
      total_processed: number;
    };
    failed_contacts?: Array<{
      invitee_id: number;
      contact_name: string;
      error: string;
    }>;
  }> {
    return this.post(
      `/invitees/events/${eventId}/send-final-confirmations`,
      inviteeIds ? { invitee_ids: inviteeIds } : undefined
    );
  }

  async sendFinalConfirmationsSmart(eventId: number): Promise<{
    success: boolean;
    message: string;
    stats: {
      sent: number;
      failed: number;
      total_eligible: number;
      reasons: {
        never_sent: number;
        email_changed: number;
      };
    };
    failed_contacts?: Array<{
      invitee_id: number;
      contact_name: string;
      error: string;
    }>;
  }> {
    return this.post(`/events/${eventId}/send-final-confirmations-smart`);
  }

  async resendFinalConfirmation(inviteeId: number): Promise<{
    success: boolean;
    message: string;
    sent_to_email: string;
  }> {
    return this.post(`/invitees/${inviteeId}/resend-final-confirmation`);
  }

  async exportEventInvitees(eventId: number): Promise<Blob> {
    const response = await this.instance.get(`/events/${eventId}/export/invitees`, {
      responseType: 'blob',
    });

    return response.data;
  }

  async getEventVIPSummary(eventId: number): Promise<any> {
    return this.get(`/invitees/events/${eventId}/vip-summary`);
  }

  // CRM Sync API methods
  async initiateCRMSync(): Promise<CRMSyncResponse> {
    return this.post('/crm/sync');
  }

  async getCRMSyncLogs(params?: {
    page?: number;
    page_size?: number;
    sync_type?: string;
    status?: string;
    initiated_by?: number;
    date_from?: string;
    date_to?: string;
    sort_by?: string;
    sort_order?: string;
  }): Promise<PaginatedResponse<CRMSyncLog>> {
    return this.get('/crm/sync-logs', params);
  }

  async getCRMSyncLog(id: number): Promise<CRMSyncLog> {
    return this.get(`/crm/sync-logs/${id}`);
  }

  async cancelCRMSync(id: number): Promise<MessageResponse> {
    return this.post(`/crm/sync-logs/${id}/cancel`);
  }

  async getCRMSyncStats(): Promise<CRMSyncStats> {
    return this.get('/crm/sync-stats');
  }

  async getCRMSyncPreview(sync_type: string = 'contacts', limit: number = 10): Promise<any> {
    return this.get('/crm/preview', { sync_type, limit });
  }

  async testCRMConnection(): Promise<MessageResponse> {
    return this.get('/crm/connection-test');
  }

  async getCRMContactsSummary(): Promise<any> {
    return this.get('/crm/contacts-summary');
  }

  async forceContactSync(contactId: number): Promise<MessageResponse> {
    return this.post(`/crm/force-contact-sync/${contactId}`);
  }

  // RSVP API methods (public, no auth required)
  async getRSVPForm(token: string): Promise<any> {
    return this.get(`/invitees/rsvp/${token}`);
  }

  async submitRSVP(token: string, data: any): Promise<any> {
    return this.post(`/invitees/rsvp/${token}`, data);
  }

  // ============================================
  // CHECK-IN ENDPOINTS
  // ============================================

  /**
   * Get check-in list for an event (authenticated)
   * Returns paginated list of accepted invitees with check-in status
   */
  async getCheckInList(
    eventId: number,
    params?: {
      page?: number;
      page_size?: number;
      q?: string;
      status_filter?: string;
    }
  ): Promise<{
    items: Array<{
      invitee_id: number;
      contact_name: string;
      contact_email?: string;
      organization?: string;
      vip_status: VIPStatus;
      checked_in: boolean;
      checked_in_at?: string;
      protocol_rank?: number;
      official_title?: string;
    }>;
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
    stats: {
      total_accepted: number;
      checked_in: number;
      pending: number;
    };
  }> {
    return this.get(`/checkin/events/${eventId}/checkin-list`, params);
  }

  /**
   * Manual check-in for an invitee (authenticated)
   */
  async checkInInvitee(
    eventId: number,
    inviteeId: number
  ): Promise<{
    success: boolean;
    message: string;
    contact_name: string;
    organization?: string;
    vip_status: VIPStatus;
    checked_in_at: string;
    notifications_sent: number;
    already_checked_in?: boolean;
  }> {
    return this.post(`/checkin/events/${eventId}/checkin/${inviteeId}`);
  }

  /**
 * Cancel/undo check-in for an invitee (authenticated)
 */
async cancelCheckIn(
  eventId: number,
  inviteeId: number
): Promise<{
  success: boolean;
  message: string;
  contact_name: string;
  organization?: string;
  vip_status: VIPStatus;
  checked_in_at: string;
  notifications_sent: number;
  already_checked_in: boolean;
}> {
  return this.delete(`/checkin/events/${eventId}/checkin/${inviteeId}`);
}

  /**
   * Get VIP board - PUBLIC endpoint (no auth required)
   */
  async getVIPBoard(uuid: string): Promise<{
    event: {
      title: string;
      date: string;
      location?: string;
    };
    stats: {
      total_vips: number;
      checked_in: number;
      total_accepted: number;
      checked_in_total: number;
    };
    vips: Array<{
      name: string;
      official_title?: string;
      organization?: string;
      vip_status: VIPStatus;
      checked_in: boolean;
      checked_in_at?: string;
      protocol_rank?: number;
    }>;
  }> {
    return this.get(`/checkin/vip-board/${uuid}`);
  }

  /**
   * Search guests on VIP board - PUBLIC endpoint (no auth required)
   */
  async searchVIPBoard(
    uuid: string,
    query: string
  ): Promise<{
    results: Array<{
      name: string;
      organization?: string;
      vip_status: VIPStatus;
      checked_in: boolean;
      checked_in_at?: string;
    }>;
  }> {
    return this.get(`/checkin/vip-board/${uuid}/search`, { q: query });
  }

  // --- Per-event emails (editor, #8) ---
  async getEventEmails(eventId: number): Promise<EventEmail[]> {
    return this.get(`/events/${eventId}/emails`);
  }

  async saveEventEmail(eventId: number, emailId: number, content: EventEmailContent): Promise<EventEmail> {
    return this.put(`/events/${eventId}/emails/${emailId}`, content);
  }

  async resetEventEmail(eventId: number, emailId: number): Promise<EventEmail> {
    return this.post(`/events/${eventId}/emails/${emailId}/reset`);
  }

  async previewEventEmail(eventId: number, emailId: number, content: EventEmailContent): Promise<EmailPreview> {
    return this.post(`/events/${eventId}/emails/${emailId}/preview`, content);
  }

  async sendTestEventEmail(eventId: number, emailId: number): Promise<{ sent_to: string; subject: string }> {
    return this.post(`/events/${eventId}/emails/${emailId}/test-send`);
  }

  async createEventEmail(eventId: number, kind: CreatableEmailKind): Promise<EventEmail> {
    return this.post(`/events/${eventId}/emails`, { kind });
  }

  async deleteEventEmail(eventId: number, emailId: number): Promise<void> {
    await this.instance.delete(`/events/${eventId}/emails/${emailId}`);
  }

  async getEmailAudiences(eventId: number, emailId: number, vipOnly: boolean): Promise<AudienceCount[]> {
    return this.get(`/events/${eventId}/emails/${emailId}/audiences`, { vip_only: vipOnly });
  }

  async getEmailSends(eventId: number, emailId: number): Promise<EmailSend[]> {
    return this.get(`/events/${eventId}/emails/${emailId}/sends`);
  }

  async sendEventEmail(eventId: number, emailId: number, body: EmailSendRequest): Promise<EmailSend> {
    return this.post(`/events/${eventId}/emails/${emailId}/send`, body);
  }

  async getEmailMergeFields(eventId: number): Promise<EmailMergeField[]> {
    return this.get(`/events/${eventId}/email-merge-fields`);
  }

  // Staging-only check-in simulation (#58): 404 in production.
  async getCheckinSimulation(): Promise<CheckinSimulationStatus> {
    return this.get('/demo/checkin-simulation');
  }

  async startCheckinSimulation(body: CheckinSimulationRequest): Promise<{ event_id: number; vip_board_url: string }> {
    return this.post('/demo/checkin-simulation', body);
  }

  async stopCheckinSimulation(): Promise<any> {
    return this.delete('/demo/checkin-simulation');
  }

  async getEmailPalette(): Promise<EmailPalette> {
    return this.get('/email-palette');
  }

  async updateEmailPalette(palette: EmailPalette): Promise<EmailPalette> {
    return this.put('/email-palette', palette);
  }

  async getEmailBranding(eventId: number): Promise<EmailBranding> {
    return this.get(`/events/${eventId}/email-branding`);
  }

  async uploadEmailImage(eventId: number, slot: 'header' | 'footer', file: File): Promise<EmailBranding> {
    const form = new FormData();
    form.append('file', file);
    const response: AxiosResponse<EmailBranding> = await this.instance.post(
      `/events/${eventId}/email-branding/${slot}`, form, { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async resetEmailImage(eventId: number, slot: 'header' | 'footer'): Promise<EmailBranding> {
    return this.delete(`/events/${eventId}/email-branding/${slot}`);
  }

  // --- Account manager (#70, admin only) ---

  async updateProfile(data: { first_name?: string; last_name?: string; phone?: string | null }): Promise<User> {
    return this.put('/auth/me', data);
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<MessageResponse> {
    return this.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword });
  }

  async listUsers(): Promise<User[]> {
    const page = await this.get<PaginatedResponse<User>>('/users/', { page: 1, page_size: 100 });
    return page.items;
  }

  async createUser(data: AdminUserCreate): Promise<UserWithTemporaryPassword> {
    return this.post('/users/', data);
  }

  async updateUser(userId: number, data: AdminUserUpdate): Promise<User> {
    return this.put(`/users/${userId}`, data);
  }

  async deactivateUser(userId: number): Promise<MessageResponse> {
    return this.delete(`/users/${userId}`);
  }

  async activateUser(userId: number): Promise<MessageResponse> {
    return this.post(`/users/${userId}/activate`);
  }

  async resetUserPassword(userId: number): Promise<{ temporary_password: string }> {
    return this.post(`/users/${userId}/reset-password`);
  }

  async getUserEvents(userId: number): Promise<AssignedEvent[]> {
    return this.get(`/users/${userId}/events`);
  }

  async assignUserToEvent(userId: number, eventId: number): Promise<MessageResponse> {
    return this.post(`/users/${userId}/events/${eventId}`);
  }

  async unassignUserFromEvent(userId: number, eventId: number): Promise<MessageResponse> {
    return this.delete(`/users/${userId}/events/${eventId}`);
  }

  async getEventTeam(eventId: number): Promise<TeamMember[]> {
    return this.get(`/events/${eventId}/team`);
  }

  async addEventTeamMember(eventId: number, userId: number): Promise<MessageResponse> {
    return this.post(`/events/${eventId}/team/${userId}`);
  }

  async removeEventTeamMember(eventId: number, userId: number): Promise<MessageResponse> {
    return this.delete(`/events/${eventId}/team/${userId}`);
  }

  /**
   * Check-in via QR code (authenticated)
   */
  async checkInByQR(invitationToken: string): Promise<{
    success: boolean;
    message: string;
    contact_name: string;
    organization?: string;
    vip_status: VIPStatus;
    checked_in_at: string;
    notifications_sent: number;
    already_checked_in?: boolean;
  }> {
    return this.post('/checkin/qr', {
      invitation_token: invitationToken,
    });
  }
}

export interface ActiveEventStats {
  id: number;
  title: string;
  event_date: string;
  event_end_date: string | null;
  location: string | null;
  total_invitees: number;
  response_rate: number;
  accepted: number;
}

export const apiService = new ApiService();